import type { BootConfig } from "./BootConfig";
import type { GameBackgroundDefinition, GameBootDefinition } from "../composition/GameDefinition";
import type { BootLaunchPayload } from "./BootLaunchPayload";
import { AssetRegistry } from "../assets/AssetRegistry";
import { BootProgress } from "./BootProgress";
import type { BootLoaderPort } from "./loader/BootLoaderPort";
import { BACKEND_API_PATHS } from "./defaults/BackendApiPaths";
import { SHELL_BUNDLE_NAMES } from "./defaults/BundleNames";
import { resolveBootVisualProfile } from "./profile/BootVisualProfile";
import { resolveEffectiveBackendBaseUrl } from "./utils/backendUrl";
import { LaunchContextSourceMode } from "./utils/launchContextSource";
import { readQueryParam } from "./utils/readQueryParam";
import { resolveOperatorId } from "../branding/OperatorIdResolver";
import { OperatorBrandingService } from "../branding/OperatorBrandingService";
import { resolveEarlyBranding } from "./earlyBranding";
import { createBootLocalization } from "./bootLocalization";
import { loadCoreBundles, loadCurrencyCatalog, loadInitialSettings, loadLaunchContext, startPlaySession } from "./bootLoads";
import { readGameLink } from "./launch/readGameLink";
import { retryWithBackoff } from "./retry/RetryWithBackoff";
import { LaunchRefusedError } from "./session/LaunchRefusedError";
import type { LocalizationService } from "../localization/LocalizationService";
import { launchRefusedFailure } from "../system-modal/failureCopy";
import type { Log } from "@slot/presentation";

export interface BootGameInput {
  /** What the game contributes to boot: locale, fixture settings, extra bundles (`GameDefinition.boot`). */
  game: GameBootDefinition;
  config: BootConfig;
  /** The loader already on screen from `index.html`; null when the markup is absent or skipped. */
  loader: BootLoaderPort | null;
  /** The title's background stills (`GameDefinition.background`, decision 69): the loader shows them; absent, `background-intro`. */
  background?: GameBackgroundDefinition;
  log: Log;
}

export interface BootGameResult {
  payload: BootLaunchPayload;
}

/** Branded loader art arriving later than this shows the default loader. */
const EARLY_BRANDING_TIMEOUT_MS = 4000;

/**
 * The boot sequence, every title the same:
 *
 * 1. the backend URL (config + `?backend=` in dev)
 * 2. the asset manifest → the loader's art by URL (branded when the operator's arrives in time)
 * 3. the visual profile
 * 4. in parallel: the play session then the initial settings (decision 80), launch context (`?device=`), currency
 *    catalog, the core bundles
 * 5. localization
 * 6. operator branding (the early manifest reused when the id matches)
 * 7. `?replay=` → the payload
 *
 * Every network step retries three times with the loader showing the attempt; a payload that fails
 * validation is not retried. A boot that still fails rejects into the boot boundary (`startGame`),
 * which shows the boot-failure popup. Offline mode
 * (`backendMode: Offline` or `?backend=scripted`) uses the embedded fixtures for every backend call.
 */
export async function bootGame(input: BootGameInput): Promise<BootGameResult> {
  const { config, log } = input;
  const queryString = window.location.search;

  const backend = resolveEffectiveBackendBaseUrl({ devModeEnabled: config.devModeEnabled, backendMode: config.backendMode, localhostUrl: config.localhostUrl, deployedUrl: config.deployedUrl, queryString, log });
  const launchContextSourceMode = backend.forceFixtureLaunchMode ? LaunchContextSourceMode.Fixture : config.launchContextSourceMode;

  // The loader is already painted — `index.html` carries it, so it was on screen before this bundle
  // finished downloading (decision 19). It reports from here, BEFORE the manifest fetch: until
  // 2026-09-23 the loader could not exist until the manifest and ~295 KB of art had landed, so the
  // whole of that wait was a blank page with nothing to say.
  const loader = input.loader;
  const progress = new BootProgress(loader, config.devModeEnabled);
  progress.stage("connecting", "boot: begin");

  const assets = await retryWithBackoff({ taskName: "asset_manifest", log, run: () => AssetRegistry.load(config.assetsBaseUrl) });
  const brandingBaseUrl = config.operatorManifestBaseUrl.trim() || config.assetsBaseUrl;
  const gameVersion = config.gameVersion.trim() || undefined;
  const earlyOperatorId = resolveOperatorId({ configOverride: config.operatorIdOverride, queryString });
  // The manifest's file table is enough to point an <img> at the default art: no bundle download,
  // no texture decode, and the art fades in over a loader the player is already looking at.
  if (loader && !config.skipLoader) {
    const boot = (path: string): string | null => assets.resolveUrl(SHELL_BUNDLE_NAMES.bootAssets, path);
    const stills = input.background?.stills;
    loader.setArt({
      backgroundUrl: boot(stills?.landscape ?? "background-intro"),
      backgroundPortraitUrl: stills ? boot(stills.portrait) : null,
      logoUrl: boot("logo"),
    });
  }
  const [, earlyBranding] = await Promise.all([
    config.skipLoader ? null : preloadBootLogo(assets, log),
    resolveEarlyBranding({ assets, operatorId: earlyOperatorId, manifestBaseUrl: brandingBaseUrl, gameVersion, timeoutMs: EARLY_BRANDING_TIMEOUT_MS, log }),
  ]);
  if (loader && (earlyBranding?.loaderBackgroundUrl || earlyBranding?.loaderLogoUrl)) {
    // An operator's background is one image for every viewport.
    loader.setArt({ backgroundUrl: earlyBranding.loaderBackgroundUrl, backgroundPortraitUrl: earlyBranding.loaderBackgroundUrl ? null : undefined, logoUrl: earlyBranding.loaderLogoUrl });
  }

  const profile = resolveBootVisualProfile({ forcedProfile: config.forcedVisualProfile || null });
  log(`[BootProfile] selected profile=${profile.profile} reason=${profile.reason}`);
  progress.stage("profile", `profile: ${profile.profile} ${profile.reason}`);
  const visualBundleName = profile.profile === "low" ? SHELL_BUNDLE_NAMES.visualsLow : SHELL_BUNDLE_NAMES.visualsHigh;
  // The profile bundles are optional: this game ships them empty.
  const coreBundles = [SHELL_BUNDLE_NAMES.sharedCore, SHELL_BUNDLE_NAMES.gameSharedVisuals, SHELL_BUNDLE_NAMES.shellUi, ...input.game.extraBundles, ...(assets.hasBundle(visualBundleName) ? [visualBundleName] : [])];

  const replayRoundId = readQueryParam(queryString, "replay");
  if (replayRoundId) log(`replay mode detected — roundId="${replayRoundId}"`);
  // The casino's game link (decision 81): the launch the session starts with, a free-play currency, the language.
  const link = readGameLink(queryString, log);

  const ctx = { backendBaseUrl: backend.baseUrl, config, queryString, progress, log };
  const localize = (settingsLocaleCode: string): Promise<LocalizationService> =>
    createBootLocalization({ game: input.game, assets, localeCodeOverride: config.localeCodeOverride, linkLocaleCode: link.lang, settingsLocaleCode, log });
  const bundles = loadCoreBundles(ctx, assets, coreBundles);
  // The play session before the settings (decision 80): starting it ends a tab left open elsewhere, so the settings read
  // after it hold everything that tab did, its last round included. They are the player's, asked for with its bearer
  // (decision 81). A refused launch ends the boot in the link's language.
  const sessionThenSettings = startPlaySession(ctx, { replay: replayRoundId !== null, link })
    .then(async (playSession) => ({ playSession, initialSettings: await loadInitialSettings(ctx, input.game, playSession) }))
    .catch(async (error: unknown) => {
      throw error instanceof LaunchRefusedError ? await inTheLinksWords(error, bundles, localize) : error;
    });
  const [{ playSession, initialSettings }, launchContext, currencyCatalog] = await Promise.all([
    sessionThenSettings,
    loadLaunchContext(ctx, launchContextSourceMode),
    loadCurrencyCatalog(ctx),
    bundles,
  ]);
  const localization = await localize(initialSettings.localeCode);

  const operatorId = resolveOperatorId({ configOverride: config.operatorIdOverride, launchContext, initialSettings, queryString });
  progress.stage("branding", `branding: ${operatorId ?? "none"}`);
  const branding = await new OperatorBrandingService({ assets, sharedBundle: SHELL_BUNDLE_NAMES.gameSharedVisuals, manifestBaseUrl: brandingBaseUrl, gameVersion, log }).resolve(
    operatorId,
    earlyBranding && operatorId === earlyOperatorId ? { manifest: earlyBranding.manifest } : undefined,
  );
  progress.stage("branded", `branding: ${branding.operatorBundle ? `${operatorId} → ${branding.operatorBundle}` : `${operatorId ?? "none"} (unbranded)`}`);

  const payload: BootLaunchPayload = {
    localization,
    devModeEnabled: config.devModeEnabled,
    gameVersion: gameVersion ?? "0.0.0",
    initialSettings,
    launchContext,
    currencyCatalog,
    backendConfig: {
      baseUrl: backend.baseUrl,
      roundRequestPath: BACKEND_API_PATHS.roundRequest,
      presentationCompletePath: BACKEND_API_PATHS.presentationComplete,
      replayPath: BACKEND_API_PATHS.replay,
      gambleRequestPath: BACKEND_API_PATHS.gambleRequest,
      gambleCompletePath: BACKEND_API_PATHS.gambleComplete,
      historyPath: BACKEND_API_PATHS.history,
    },
    playSession,
    assets,
    sharedCoreBundleName: SHELL_BUNDLE_NAMES.sharedCore,
    sharedBundleName: SHELL_BUNDLE_NAMES.gameSharedVisuals,
    visualBundleName,
    operatorId,
    operatorManifest: branding.manifest,
    brandedAssets: branding.resolver,
    replayContext: replayRoundId ? { roundId: replayRoundId } : null,
    skipIntro: config.skipIntro,
  };
  progress.stage("opening", "scene: load");
  log("boot complete");
  return { payload };
}

/**
 * A refused launch in the link's language (decision 81): the shell's dictionary is read once the core bundles are in.
 * When they cannot load, the refusal keeps the shell's English.
 */
async function inTheLinksWords(refusal: LaunchRefusedError, bundles: Promise<void>, localize: (settingsLocaleCode: string) => Promise<LocalizationService>): Promise<LaunchRefusedError> {
  try {
    await bundles;
    const localization = await localize("");
    const copy = launchRefusedFailure();
    return refusal.worded({ title: localization.t(copy.titleKey, copy.titleFallback), message: localization.t(copy.messageKey, copy.messageFallback) });
  } catch {
    return refusal;
  }
}

/**
 * The boot bundle's logo as a texture: the in-game logo draws it (`BrandedIconView`). Nothing else there is decoded into
 * Pixi (decision 69): the loader and the intro splash show their art by URL, and a background still is decoded only as
 * the scene first shows it. Loaded alongside the branding race rather than later on the critical path; `?skipLoader=1`
 * skips it, and with it the in-game logo, as before.
 */
async function preloadBootLogo(assets: AssetRegistry, log: Log): Promise<void> {
  try {
    await assets.loadAsset(SHELL_BUNDLE_NAMES.bootAssets, "logo");
  } catch (error) {
    log(`boot-assets logo preload failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}
